import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  VStack,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faChevronDown } from '@fortawesome/free-solid-svg-icons';

const PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '6m', label: 'Last 6 months' },
  { value: '12m', label: 'Last 12 months' },
];

function formatDateForInput(date) {
  return date.toISOString().split('T')[0];
}

function formatDateForDisplay(date) {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getPresetDates(preset) {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  let start = new Date();
  start.setHours(0, 0, 0, 0);

  switch (preset) {
    case 'today':
      break;
    case 'yesterday':
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      break;
    case '7d':
      start.setDate(start.getDate() - 6);
      break;
    case '30d':
      start.setDate(start.getDate() - 29);
      break;
    case '90d':
      start.setDate(start.getDate() - 89);
      break;
    case '6m':
      start.setMonth(start.getMonth() - 6);
      break;
    case '12m':
      start.setFullYear(start.getFullYear() - 1);
      break;
    default:
      start.setDate(start.getDate() - 6);
  }

  return { start, end };
}

function getComparisonDates(start, end) {
  const duration = end.getTime() - start.getTime();
  const compEnd = new Date(start.getTime() - 1);
  compEnd.setHours(23, 59, 59, 999);
  const compStart = new Date(compEnd.getTime() - duration);
  compStart.setHours(0, 0, 0, 0);
  return { start: compStart, end: compEnd };
}

function DateRangePicker({
  value = 'today',
  onChange,
  showCompare = true,
  compareEnabled = false,
  onCompareChange,
  size = 'md',
}) {
  // Initialize state based on whether value is a preset string or custom date range object
  const getInitialState = () => {
    if (value && typeof value === 'object' && value.start && value.end) {
      // It's a custom date range object - extract the preset and dates
      const startDate = value.start instanceof Date ? value.start : new Date(value.start);
      const endDate = value.end instanceof Date ? value.end : new Date(value.end);
      return {
        preset: value.preset || 'custom',
        customStart: formatDateForInput(startDate),
        customEnd: formatDateForInput(endDate),
      };
    }
    // It's a preset string
    return {
      preset: value || 'today',
      customStart: '',
      customEnd: '',
    };
  };

  const initialState = getInitialState();
  const [selectedPreset, setSelectedPreset] = useState(initialState.preset);
  const [customStart, setCustomStart] = useState(initialState.customStart);
  const [customEnd, setCustomEnd] = useState(initialState.customEnd);
  const [tempStart, setTempStart] = useState('');
  const [tempEnd, setTempEnd] = useState('');
  const { isOpen: isCustomOpen, onOpen: onCustomOpen, onClose: onCustomClose } = useDisclosure();
  const buttonRef = useRef(null);

  const menuBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const activeBg = useColorModeValue('brand.50', 'brand.900');
  const activeColor = useColorModeValue('brand.600', 'brand.200');

  // Calculate current date range
  const dateRange = useMemo(() => {
    if (selectedPreset === 'custom' && customStart && customEnd) {
      return {
        start: new Date(customStart + 'T00:00:00'),
        end: new Date(customEnd + 'T23:59:59'),
      };
    }
    return getPresetDates(selectedPreset);
  }, [selectedPreset, customStart, customEnd]);

  // Calculate comparison date range
  const comparisonRange = useMemo(() => {
    if (!compareEnabled) return null;
    return getComparisonDates(dateRange.start, dateRange.end);
  }, [dateRange, compareEnabled]);

  // Store callback in ref to avoid re-render loops
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Notify parent of changes
  useEffect(() => {
    if (onChangeRef.current) {
      onChangeRef.current({
        preset: selectedPreset,
        start: dateRange.start,
        end: dateRange.end,
        comparison: comparisonRange,
      });
    }
  }, [dateRange, comparisonRange, selectedPreset]);

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setCustomStart('');
    setCustomEnd('');
  };

  const handleCustomClick = () => {
    const { start, end } = customStart && customEnd
      ? { start: new Date(customStart), end: new Date(customEnd) }
      : getPresetDates('7d');
    setTempStart(formatDateForInput(start));
    setTempEnd(formatDateForInput(end));
    onCustomOpen();
  };

  const handleCustomApply = () => {
    if (tempStart && tempEnd && new Date(tempStart) <= new Date(tempEnd)) {
      setCustomStart(tempStart);
      setCustomEnd(tempEnd);
      setSelectedPreset('custom');
      onCustomClose();
    }
  };

  const handleCustomCancel = () => {
    onCustomClose();
  };

  const getDisplayLabel = () => {
    if (selectedPreset === 'custom' && customStart && customEnd) {
      const start = new Date(customStart + 'T00:00:00');
      const end = new Date(customEnd + 'T23:59:59');
      return `${formatDateForDisplay(start)} - ${formatDateForDisplay(end)}`;
    }
    const preset = PRESETS.find((p) => p.value === selectedPreset);
    return preset?.label || 'Select range';
  };

  const maxDate = formatDateForInput(new Date());

  return (
    <HStack spacing={3}>
      {showCompare && (
        <Checkbox
          isChecked={compareEnabled}
          onChange={(e) => onCompareChange?.(e.target.checked)}
          size={size}
          colorScheme="brand"
        >
          <Text fontSize="sm" whiteSpace="nowrap">Compare to previous period</Text>
        </Checkbox>
      )}

      <Popover
        isOpen={isCustomOpen}
        onClose={onCustomClose}
        placement="bottom-start"
        closeOnBlur={true}
        initialFocusRef={undefined}
      >
        <PopoverTrigger>
          <Box ref={buttonRef}>
            <Menu>
              <MenuButton
                as={Button}
                leftIcon={<FontAwesomeIcon icon={faCalendar} />}
                rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                variant="outline"
                size={size}
                borderRadius="xl"
                fontWeight="500"
                minW="180px"
              >
                <Text noOfLines={1}>{getDisplayLabel()}</Text>
              </MenuButton>
              <MenuList bg={menuBg} borderColor={borderColor} borderRadius="xl" py={2} minW="200px">
                {PRESETS.map((preset) => (
                  <MenuItem
                    key={preset.value}
                    onClick={() => handlePresetSelect(preset.value)}
                    bg={selectedPreset === preset.value ? activeBg : 'transparent'}
                    color={selectedPreset === preset.value ? activeColor : 'inherit'}
                    fontWeight={selectedPreset === preset.value ? '600' : '400'}
                    _hover={{ bg: selectedPreset === preset.value ? activeBg : hoverBg }}
                    borderRadius="lg"
                    mx={2}
                    w="calc(100% - 16px)"
                  >
                    {preset.label}
                  </MenuItem>
                ))}
                <MenuDivider />
                <MenuItem
                  onClick={handleCustomClick}
                  bg={selectedPreset === 'custom' ? activeBg : 'transparent'}
                  color={selectedPreset === 'custom' ? activeColor : 'inherit'}
                  fontWeight={selectedPreset === 'custom' ? '600' : '400'}
                  _hover={{ bg: selectedPreset === 'custom' ? activeBg : hoverBg }}
                  borderRadius="lg"
                  mx={2}
                  w="calc(100% - 16px)"
                >
                  Custom range...
                </MenuItem>
              </MenuList>
            </Menu>
          </Box>
        </PopoverTrigger>
        <PopoverContent
          bg={menuBg}
          borderColor={borderColor}
          borderRadius="xl"
          p={4}
          w="280px"
          boxShadow="lg"
        >
          <PopoverBody p={0}>
            <VStack spacing={4} align="stretch">
              <Text fontWeight="600" fontSize="sm">Custom Date Range</Text>
              <FormControl>
                <FormLabel fontSize="sm">Start Date</FormLabel>
                <Input
                  type="date"
                  value={tempStart}
                  onChange={(e) => setTempStart(e.target.value)}
                  max={tempEnd || maxDate}
                  size="sm"
                  borderRadius="lg"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">End Date</FormLabel>
                <Input
                  type="date"
                  value={tempEnd}
                  onChange={(e) => setTempEnd(e.target.value)}
                  min={tempStart}
                  max={maxDate}
                  size="sm"
                  borderRadius="lg"
                />
              </FormControl>
            </VStack>
          </PopoverBody>
          <PopoverFooter border="0" pt={4} pb={0} px={0}>
            <HStack justify="flex-end" spacing={2}>
              <Button size="sm" variant="ghost" onClick={handleCustomCancel} borderRadius="lg">
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="brand"
                onClick={handleCustomApply}
                borderRadius="lg"
                isDisabled={!tempStart || !tempEnd || new Date(tempStart) > new Date(tempEnd)}
              >
                Apply
              </Button>
            </HStack>
          </PopoverFooter>
        </PopoverContent>
      </Popover>
    </HStack>
  );
}

export default DateRangePicker;
